import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Box, Button, Paper, Tabs, Tab, TextField, MenuItem,
  Switch, FormControlLabel, CircularProgress, Alert, Snackbar,
  Divider, IconButton, Chip, Slider, Card, CardContent,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  alpha, useTheme, Stack, Grid, Fade, Collapse,
} from '@mui/material';
import type { TextFieldProps } from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import TopicIcon from '@mui/icons-material/Topic';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BarChartIcon from '@mui/icons-material/BarChart';
import HistoryIcon from '@mui/icons-material/History';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TuneIcon from '@mui/icons-material/Tune';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DraftsIcon from '@mui/icons-material/Drafts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FolderIcon from '@mui/icons-material/Folder';
import ArticleIcon from '@mui/icons-material/Article';
import EditNoteIcon from '@mui/icons-material/EditNote';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useNavigate, useParams } from 'react-router-dom';
import { getAreaStyle, getAreaIcon } from '../../components/documents/areaConfig';
import * as flowService from '../../services/documentFlowService';
import type { DocumentFlow, StepField, DynamicStep } from '../../services/documentFlowService';
import { STEP_TEMPLATES, migrateStepsConfig } from '../../services/documentFlowService';
import { AVAILABLE_FORMATS } from '../../utils/formatValidators';

interface TabPanelProps { children?: React.ReactNode; value: number; index: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && <Fade in><Box sx={{ py: 3 }}>{children}</Box></Fade>}</div>
);

const FIELD_TYPES = [
  { value: 'text', label: 'Texto curto' },
  { value: 'textarea', label: 'Texto longo' },
  { value: 'select', label: 'Seleção' },
  { value: 'multiselect', label: 'Multi-seleção' },
  { value: 'date', label: 'Data' },
  { value: 'number', label: 'Número' },
  { value: 'currency', label: 'Moeda (R$)' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file', label: 'Arquivo' },
  { value: 'tags', label: 'Tags (etiquetas)' },
  { value: 'repeatable_group', label: 'Grupo repetível' },
];

const emptyFlow: Partial<DocumentFlow> = {
  name: '', slug: '', description: '', category: 'pecas_juridicas', area: 'civel', areas: [],
  status: 'draft', is_featured: false, display_order: 0, is_approved: false,
  steps_config: { steps: [] },
  ai_context: '', ai_prompt_hints: '', ai_system_instructions: '', ai_example_output: '',
  ai_document_structure: '', ai_prefill_enabled: true, ai_prefill_instructions: '',
  ai_upload_analysis_enabled: true, ai_upload_analysis_instructions: '',
  generation_mode: 'simple', ai_validation_enabled: false, ai_temperature_preset: 'balanced',
  ai_max_tokens: 12000, ai_context_extraction_focus: '', default_writing_style: 'formal',
};

const AREA_OPTIONS = [
  { value: 'civel', label: 'Cível' },
  { value: 'trabalhista', label: 'Trabalhista' },
  { value: 'criminal', label: 'Criminal' },
  { value: 'tributario', label: 'Tributário' },
  { value: 'administrativo', label: 'Administrativo' },
  { value: 'previdenciario', label: 'Previdenciário' },
  { value: 'familia', label: 'Família' },
  { value: 'empresarial', label: 'Empresarial' },
];

const STEP_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  form_fields: { label: 'Campos personalizados', icon: 'edit_note' },
  tribunal_selection: { label: 'Seleção de Tribunal', icon: 'gavel' },
  topics: { label: 'Tópicos Jurídicos', icon: 'topic' },
  requests: { label: 'Pedidos', icon: 'request_quote' },
};

const STEP_TYPE_ICONS: Record<string, React.ReactElement> = {
  form_fields: <EditNoteIcon />,
  tribunal_selection: <GavelIcon />,
  topics: <TopicIcon />,
  requests: <ListAltIcon />,
  description: <DescriptionIcon />,
  person: <PersonIcon />,
  person_outline: <PersonIcon />,
  attach_money: <AttachMoneyIcon />,
  folder: <FolderIcon />,
  article: <ArticleIcon />,
  gavel: <GavelIcon />,
  topic: <TopicIcon />,
  request_quote: <ListAltIcon />,
  add_box: <AddIcon />,
  edit_note: <EditNoteIcon />,
};

const reindexFields = (fields: StepField[] = []): StepField[] => (
  fields.map((field, index) => ({ ...field, order: index }))
);

const normalizeFields = (fields: StepField[] = []): StepField[] => (
  reindexFields(
    [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  )
);

const normalizeStep = (step: DynamicStep, index: number): DynamicStep => ({
  ...step,
  order: index,
  config: { ...step.config, fields: normalizeFields(step.config?.fields || []) },
});

const normalizeSteps = (steps: DynamicStep[] = []): DynamicStep[] => (
  [...steps]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((step, index) => normalizeStep(step, index))
);

const AutoGrowTextField: React.FC<TextFieldProps & {
  minRows?: number;
  maxRows?: number;
}> = ({ minRows = 3, maxRows = 12, multiline = true, sx, ...props }) => {
  const [expanded, setExpanded] = useState(false);
  const effectiveMinRows = expanded ? Math.max(minRows, 10) : minRows;
  const effectiveMaxRows = expanded ? Math.max(maxRows, 24) : maxRows;

  return (
    <Box>
      <TextField
        {...props}
        multiline={multiline}
        minRows={effectiveMinRows}
        maxRows={effectiveMaxRows}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, ...sx }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Button
          size="small"
          color="inherit"
          startIcon={expanded ? <CloseFullscreenIcon sx={{ fontSize: 14 }} /> : <OpenInFullIcon sx={{ fontSize: 14 }} />}
          onClick={() => setExpanded(prev => !prev)}
          sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
        >
          {expanded ? 'Recolher' : 'Expandir'}
        </Button>
      </Box>
    </Box>
  );
};

const SectionCard: React.FC<{
  icon: React.ReactElement; title: string; subtitle?: string; enabled?: boolean;
  onToggle?: (v: boolean) => void; children: React.ReactNode; defaultExpanded?: boolean;
}> = ({ icon, title, subtitle, enabled, onToggle, children, defaultExpanded = true }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isEnabled = enabled === undefined || enabled;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', mb: 2.5, opacity: isEnabled ? 1 : 0.6, transition: 'opacity 0.2s' }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 2, cursor: 'pointer',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
        }}
        onClick={() => setExpanded(!expanded)}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {onToggle && (
          <Switch checked={enabled} onClick={(e) => e.stopPropagation()}
            onChange={(e) => onToggle(e.target.checked)} color="primary" />
        )}
        <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>
      <Collapse in={expanded && isEnabled}>
        <Divider />
        <Box sx={{ p: 2.5 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

const DocumentFlowEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const theme = useTheme();

  const [tab, setTab] = useState(0);
  const [flow, setFlow] = useState<Partial<DocumentFlow>>(emptyFlow);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [auditLogs, setAuditLogs] = useState<flowService.FlowAuditLog[]>([]);
  const [analytics, setAnalytics] = useState<flowService.FlowAnalytics | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false);
  const [promptPreview, setPromptPreview] = useState<flowService.PromptPreview | null>(null);
  const [promptPreviewLoading, setPromptPreviewLoading] = useState(false);

  useEffect(() => { if (!isNew && id) { loadFlow(id); loadExtras(id); } }, [id]);

  const loadFlow = async (flowId: string) => {
    setLoading(true);
    try { setFlow(await flowService.getFlow(flowId)); }
    catch { setError('Erro ao carregar fluxo'); }
    finally { setLoading(false); }
  };

  const loadExtras = async (flowId: string) => {
    try {
      const [a, l] = await Promise.all([flowService.getFlowAnalytics(flowId), flowService.getFlowAuditLog(flowId)]);
      setAnalytics(a); setAuditLogs(l.logs);
    } catch {}
  };

  const [addStepDialog, setAddStepDialog] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>({});
  const [dragField, setDragField] = useState<{ stepId: string; fieldId: string } | null>(null);

  const updateField = useCallback((field: string, value: any) => setFlow(prev => ({ ...prev, [field]: value })), []);

  const autoSlug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const getSteps = useCallback((): DynamicStep[] => {
    const config = flow.steps_config as any;
    if (config?.steps && Array.isArray(config.steps)) return normalizeSteps(config.steps);
    return normalizeSteps(migrateStepsConfig(config || {}).steps);
  }, [flow.steps_config]);

  const setSteps = useCallback((steps: DynamicStep[]) => {
    setFlow(prev => ({ ...prev, steps_config: { steps: normalizeSteps(steps) } }));
  }, []);

  const addStep = useCallback((template: Partial<DynamicStep>) => {
    const steps = getSteps();
    const newStep: DynamicStep = {
      id: `step_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type: template.type || 'form_fields',
      title: template.title || 'Nova Etapa',
      description: template.description,
      icon: template.icon || 'edit_note',
      order: steps.length,
      enabled: true,
      ai_hint: template.ai_hint || undefined,
      show_when: null,
      tips: template.tips || [],
      config: template.config || { fields: [] },
    };
    setSteps([...steps, newStep]);
    setExpandedSteps(prev => ({ ...prev, [newStep.id]: true }));
    setAddStepDialog(false);
  }, [getSteps, setSteps]);

  const updateStep = useCallback((stepId: string, updates: Partial<DynamicStep>) => {
    setSteps(getSteps().map(s => s.id === stepId ? { ...s, ...updates } : s));
  }, [getSteps, setSteps]);

  const removeStep = useCallback((stepId: string) => {
    setSteps(getSteps().filter(s => s.id !== stepId).map((s, i) => ({ ...s, order: i })));
  }, [getSteps, setSteps]);

  const moveStep = useCallback((stepId: string, direction: 'up' | 'down') => {
    setFlow(prev => {
      const cfg = prev.steps_config as any;
      const steps = normalizeSteps(cfg?.steps && Array.isArray(cfg.steps) ? cfg.steps : migrateStepsConfig(cfg || {}).steps);
      const idx = steps.findIndex(s => s.id === stepId);
      if (idx < 0) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= steps.length) return prev;
      [steps[idx], steps[newIdx]] = [steps[newIdx], steps[idx]];
      return { ...prev, steps_config: { steps: normalizeSteps(steps.map((s, i) => ({ ...s, order: i }))) } };
    });
  }, []);

  const cloneStep = useCallback((stepId: string) => {
    const steps = getSteps();
    const original = steps.find(s => s.id === stepId);
    if (!original) return;
    const cloned: DynamicStep = {
      ...JSON.parse(JSON.stringify(original)),
      id: `step_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      title: `${original.title} (Cópia)`,
      order: steps.length,
    };
    setSteps([...steps, cloned]);
    setExpandedSteps(prev => ({ ...prev, [cloned.id]: true }));
  }, [getSteps, setSteps]);

  const addFieldToStep = useCallback((stepId: string) => {
    const step = getSteps().find(s => s.id === stepId);
    if (!step) return;
    const fields: StepField[] = normalizeFields(step.config?.fields || []);
    const nf: StepField = { id: `field_${Date.now()}`, label: '', type: 'text', required: false, placeholder: '', helpText: '', order: fields.length };
    updateStep(stepId, { config: { ...step.config, fields: reindexFields([...fields, nf]) } });
  }, [getSteps, updateStep]);

  const updateFieldInStep = useCallback((stepId: string, fieldId: string, updates: Partial<StepField>) => {
    const step = getSteps().find(s => s.id === stepId);
    if (!step) return;
    const fields = normalizeFields(step.config?.fields || []);
    const fieldIdx = fields.findIndex(field => field.id === fieldId);
    if (fieldIdx < 0) return;
    fields[fieldIdx] = { ...fields[fieldIdx], ...updates };
    updateStep(stepId, { config: { ...step.config, fields: reindexFields(fields) } });
  }, [getSteps, updateStep]);

  const removeFieldFromStep = useCallback((stepId: string, fieldId: string) => {
    const step = getSteps().find(s => s.id === stepId);
    if (!step) return;
    const fields = normalizeFields(step.config?.fields || []).filter((field: StepField) => field.id !== fieldId);
    updateStep(stepId, { config: { ...step.config, fields: reindexFields(fields) } });
  }, [getSteps, updateStep]);

  const moveFieldInStep = useCallback((stepId: string, fieldId: string, direction: 'up' | 'down') => {
    setFlow(prev => {
      const cfg = prev.steps_config as any;
      const steps = normalizeSteps(cfg?.steps && Array.isArray(cfg.steps) ? cfg.steps : migrateStepsConfig(cfg || {}).steps);
      const step = steps.find(s => s.id === stepId);
      if (!step) return prev;
      const fields = normalizeFields(step.config?.fields || []);
      const fromIdx = fields.findIndex(f => f.id === fieldId);
      if (fromIdx < 0) return prev;
      const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
      if (toIdx < 0 || toIdx >= fields.length) return prev;
      const [moved] = fields.splice(fromIdx, 1);
      fields.splice(toIdx, 0, moved);
      const newSteps = steps.map(s =>
        s.id === stepId ? { ...s, config: { ...s.config, fields: reindexFields(fields) } } : s
      );
      return { ...prev, steps_config: { steps: normalizeSteps(newSteps) } };
    });
  }, []);

  const moveFieldInStepToIndex = useCallback((stepId: string, fieldId: string, targetIdx: number) => {
    setFlow(prev => {
      const cfg = prev.steps_config as any;
      const steps = normalizeSteps(cfg?.steps && Array.isArray(cfg.steps) ? cfg.steps : migrateStepsConfig(cfg || {}).steps);
      const step = steps.find(s => s.id === stepId);
      if (!step) return prev;
      const fields = normalizeFields(step.config?.fields || []);
      const fromIdx = fields.findIndex(f => f.id === fieldId);
      if (fromIdx < 0 || targetIdx < 0 || targetIdx >= fields.length || fromIdx === targetIdx) return prev;
      const [moved] = fields.splice(fromIdx, 1);
      fields.splice(targetIdx, 0, moved);
      const newSteps = steps.map(s =>
        s.id === stepId ? { ...s, config: { ...s.config, fields: reindexFields(fields) } } : s
      );
      return { ...prev, steps_config: { steps: normalizeSteps(newSteps) } };
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const created = await flowService.createFlow(flow);
        setSnackbar({ open: true, message: 'Fluxo criado com sucesso!', severity: 'success' });
        navigate(`/admin/document-flows/${created.id}`, { replace: true });
      } else if (id) {
        setFlow(await flowService.updateFlow(id, flow));
        setSnackbar({ open: true, message: 'Fluxo salvo com sucesso!', severity: 'success' });
        loadExtras(id);
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || 'Erro ao salvar', severity: 'error' });
    } finally { setSaving(false); }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
      <CircularProgress size={48} /><Typography color="text.secondary">Carregando fluxo...</Typography>
    </Box>
  );
  if (error) return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={0}
        sx={{
          p: 2.5, mb: 3, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
        }}>
        <IconButton onClick={() => navigate('/admin/document-flows')} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {flow.area && (
              <Box sx={{
                width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(getAreaStyle(flow.area || 'geral').color, 0.1),
              }}>
                {getAreaIcon(flow.area || 'geral', 24)}
              </Box>
            )}
            <Typography variant="h5" fontWeight={800}>
              {isNew ? 'Novo Fluxo de Documento' : flow.name}
            </Typography>
          </Box>
          {!isNew && flow.slug && (
            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>/{flow.slug}</Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {!isNew && flow.slug && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              href={`/documents/flow/${flow.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Visualizar como usuário
            </Button>
          )}
          {!isNew && flow.version && <Chip label={`v${flow.version}`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />}
          {!isNew && flow.status && (
            <Chip label={flow.status === 'active' ? 'Ativo' : flow.status === 'draft' ? 'Rascunho' : 'Inativo'}
              size="small" color={flow.status === 'active' ? 'success' : flow.status === 'draft' ? 'warning' : 'default'}
              sx={{ fontWeight: 600 }} />
          )}
          {!isNew && (
            <Tooltip title={flow.is_approved ? "Aprovado pelo time de advogados" : "Não aprovado pelo time de advogados"}>
              <Chip
                icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                label={flow.is_approved ? 'Aprovado' : 'Pendente'}
                size="small"
                color={flow.is_approved ? 'success' : 'default'}
                variant={flow.is_approved ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
                onClick={async () => {
                  try {
                    const updated = await flowService.updateFlowApproval(flow.id!, !flow.is_approved);
                    setFlow(updated);
                    setSnackbar({ open: true, message: updated.is_approved ? 'Fluxo aprovado!' : 'Aprovação removida', severity: 'success' });
                  } catch {
                    setSnackbar({ open: true, message: 'Erro ao alterar aprovação', severity: 'error' });
                  }
                }}
              />
            </Tooltip>
          )}
          <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}
            sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ mb: 0.5, borderRadius: 2.5, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{
            px: 1,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 52, gap: 0.8 },
          }}>
          <Tab icon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Informações" />
          <Tab icon={<ListAltIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Etapas" />
          <Tab icon={<SmartToyIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Configuração IA" />
          {!isNew && <Tab icon={<BarChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Análises" />}
        </Tabs>
      </Paper>

      {/* Tab 0: Basic Info */}
      <TabPanel value={tab} index={0}>
        <SectionCard icon={<DescriptionIcon />} title="Informações Básicas" subtitle="Nome, descrição e configurações gerais do fluxo">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Nome do Fluxo" value={flow.name || ''} fullWidth
                onChange={(e) => { updateField('name', e.target.value); if (isNew) updateField('slug', autoSlug(e.target.value)); }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Slug (URL)" value={flow.slug || ''} fullWidth
                onChange={(e) => updateField('slug', e.target.value)}
                InputProps={{ startAdornment: <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>/flow/</Typography> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Descrição" value={flow.description || ''} fullWidth multiline rows={2}
                onChange={(e) => updateField('description', e.target.value)}
                helperText="Uma descrição curta que aparece para o usuário ao selecionar o fluxo"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Categoria" value={flow.category || ''} select fullWidth
                onChange={(e) => updateField('category', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <MenuItem value="pecas_juridicas">Peças Jurídicas</MenuItem>
                <MenuItem value="contratos">Contratos</MenuItem>
                <MenuItem value="extrajudicial">Extrajudicial</MenuItem>
                <MenuItem value="ferramentas">Ferramentas</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Áreas do Direito"
                select
                fullWidth
                SelectProps={{
                  multiple: true,
                  value: flow.areas && flow.areas.length > 0 ? flow.areas : (flow.area ? [flow.area] : []),
                  onChange: (e) => {
                    const value = e.target.value as string[];
                    updateField('areas', value);
                    if (value.length > 0) {
                      updateField('area', value[0]);
                    }
                  },
                  renderValue: (selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={AREA_OPTIONS.find(o => o.value === value)?.label || value} size="small"
                          sx={{ bgcolor: alpha(getAreaStyle(value).color, 0.1), color: getAreaStyle(value).color, fontWeight: 600 }} />
                      ))}
                    </Box>
                  ),
                }}
                helperText="Selecione uma ou mais áreas de atuação"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {AREA_OPTIONS.map(a => (
                  <MenuItem key={a.value} value={a.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getAreaIcon(a.value, 18)}
                      {a.label}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Status" value={flow.status || 'draft'} select fullWidth
                onChange={(e) => updateField('status', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="active">Ativo</MenuItem>
                <MenuItem value="inactive">Inativo</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Estilo Padrão" value={flow.default_writing_style || 'formal'} select fullWidth
                onChange={(e) => updateField('default_writing_style', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <MenuItem value="formal">Formal</MenuItem>
                <MenuItem value="concise">Conciso</MenuItem>
                <MenuItem value="argumentative">Argumentativo</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel control={<Switch checked={flow.is_featured || false} onChange={(e) => updateField('is_featured', e.target.checked)} />}
                label={<Box><Typography variant="body2" fontWeight={600}>Destaque</Typography><Typography variant="caption" color="text.secondary">Aparece em primeiro</Typography></Box>} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Ordem de exibição" type="number" value={flow.display_order || 0} fullWidth
                onChange={(e) => updateField('display_order', parseInt(e.target.value) || 0)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
          </Grid>
        </SectionCard>
      </TabPanel>

      {/* Tab 1: Dynamic Steps */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Etapas do Fluxo</Typography>
            <Typography variant="body2" color="text.secondary">
              {getSteps().length} etapa(s) configurada(s) + 2 etapas fixas (Configuração do Documento e Revisão)
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddStepDialog(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Adicionar Etapa
          </Button>
        </Box>

        {getSteps().length === 0 && (
          <Paper variant="outlined" sx={{ py: 6, px: 3, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed', mb: 2 }}>
            <ListAltIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>Nenhuma etapa configurada</Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
              Adicione etapas para definir o formulário do fluxo
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddStepDialog(true)}
              sx={{ borderRadius: 2, textTransform: 'none' }}>
              Adicionar primeira etapa
            </Button>
          </Paper>
        )}

        {getSteps().map((step, stepIdx) => {
          const isExpanded = expandedSteps[step.id] ?? true;
          const stepIcon = STEP_TYPE_ICONS[step.icon || step.type] || <EditNoteIcon />;
          const typeLabel = STEP_TYPE_LABELS[step.type]?.label || step.type;

          return (
            <Paper key={step.id} variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', mb: 2, opacity: step.enabled ? 1 : 0.5, transition: 'opacity 0.2s' }}>
              {/* Step Header */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, p: 1.5, cursor: 'pointer',
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
              }} onClick={() => setExpandedSteps(prev => ({ ...prev, [step.id]: !isExpanded }))}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                  <IconButton size="small" disabled={stepIdx === 0}
                    onClick={(e) => { e.stopPropagation(); moveStep(step.id, 'up'); }}>
                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Typography variant="caption" fontWeight={700} color="text.disabled">{stepIdx + 1}</Typography>
                  <IconButton size="small" disabled={stepIdx === getSteps().length - 1}
                    onClick={(e) => { e.stopPropagation(); moveStep(step.id, 'down'); }}>
                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' }}>
                  {stepIcon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>{step.title}</Typography>
                    <Chip label={typeLabel} size="small" variant="outlined" sx={{ borderRadius: 1.5, fontSize: '0.7rem', height: 22 }} />
                  </Box>
                  {step.ai_hint && <Typography variant="caption" color="text.secondary" noWrap>{step.ai_hint}</Typography>}
                </Box>
                <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Duplicar"><IconButton size="small" onClick={() => cloneStep(step.id)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                  <Switch size="small" checked={step.enabled} onChange={(e) => updateStep(step.id, { enabled: e.target.checked })} />
                  <Tooltip title="Remover"><IconButton size="small" onClick={() => removeStep(step.id)}
                    sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
                <IconButton size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
              </Box>

              {/* Step Content */}
              <Collapse in={isExpanded && step.enabled}>
                <Divider />
                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={2}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField size="small" label="Título da Etapa" value={step.title} fullWidth
                          onChange={(e) => updateStep(step.id, { title: e.target.value })}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField size="small" label="Ícone" value={step.icon || ''} fullWidth
                          onChange={(e) => updateStep(step.id, { icon: e.target.value })}
                          helperText="description, gavel, person, attach_money, folder, article, edit_note"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                      </Grid>
                      <Grid item xs={12}>
                        <AutoGrowTextField size="small" label="Descrição / Instruções" value={step.description || ''} fullWidth minRows={2} maxRows={12}
                          onChange={(e) => updateStep(step.id, { description: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AutoGrowTextField size="small" label="AI Hint (instrução para o LLM)" value={step.ai_hint || ''} fullWidth minRows={3} maxRows={16}
                          onChange={(e) => updateStep(step.id, { ai_hint: e.target.value })}
                          helperText="Instrução para o LLM sobre como usar os dados desta etapa no documento"
                          InputProps={{ startAdornment: <SmartToyIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 1, mt: 0.5 }} /> }}
                        />
                      </Grid>
                    </Grid>

                    {/* Tips editor */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LightbulbIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        <Typography variant="subtitle2" fontWeight={600}>Dicas para o usuário</Typography>
                        <Button size="small" onClick={() => updateStep(step.id, { tips: [...(step.tips || []), ''] })}
                          sx={{ textTransform: 'none', ml: 'auto' }}>+ Dica</Button>
                      </Box>
                      {(step.tips || []).map((tip, tipIdx) => (
                        <Box key={tipIdx} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                          <TextField size="small" value={tip} fullWidth placeholder="Ex: Descreva os fatos em ordem cronológica"
                            onChange={(e) => {
                              const tips = [...(step.tips || [])];
                              tips[tipIdx] = e.target.value;
                              updateStep(step.id, { tips });
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                          <IconButton size="small" onClick={() => updateStep(step.id, { tips: (step.tips || []).filter((_, i) => i !== tipIdx) })}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>

                    {/* show_when */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <VisibilityIcon sx={{ fontSize: 18, color: 'info.main' }} />
                        <Typography variant="subtitle2" fontWeight={600}>Condição de exibição</Typography>
                        {!step.show_when ? (
                          <Button size="small" onClick={() => updateStep(step.id, { show_when: { step_id: '', field: '', operator: 'equals', value: '' } })}
                            sx={{ textTransform: 'none', ml: 'auto' }}>Adicionar condição</Button>
                        ) : (
                          <Button size="small" color="error" onClick={() => updateStep(step.id, { show_when: null })}
                            sx={{ textTransform: 'none', ml: 'auto' }}>Remover condição</Button>
                        )}
                      </Box>
                      {step.show_when && (
                        <Grid container spacing={1}>
                          <Grid item xs={3}>
                            <TextField size="small" label="Etapa" value={step.show_when.step_id} select fullWidth
                              onChange={(e) => updateStep(step.id, { show_when: { ...step.show_when!, step_id: e.target.value } })}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
                              <MenuItem value={step.id}><em>Esta etapa (campos acima)</em></MenuItem>
                              {getSteps().filter(s => s.id !== step.id && s.order < step.order).map(s => (
                                <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={3}>
                            <TextField size="small" label="Campo" value={step.show_when.field} fullWidth
                              onChange={(e) => updateStep(step.id, { show_when: { ...step.show_when!, field: e.target.value } })}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                          </Grid>
                          <Grid item xs={3}>
                            <TextField size="small" label="Operador" value={step.show_when.operator} select fullWidth
                              onChange={(e) => updateStep(step.id, { show_when: { ...step.show_when!, operator: e.target.value as any } })}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
                              {['equals', 'not_equals', 'contains', 'not_empty', 'is_empty'].map(op => (
                                <MenuItem key={op} value={op}>{op}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={3}>
                            <TextField size="small" label="Valor" value={step.show_when.value || ''} fullWidth
                              onChange={(e) => updateStep(step.id, { show_when: { ...step.show_when!, value: e.target.value } })}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                          </Grid>
                        </Grid>
                      )}
                    </Box>

                    <Divider />

                    {/* Type-specific config */}
                    {step.type === 'form_fields' && (
                      <Stack spacing={2} sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={2}>
                          <FormControlLabel control={<Switch size="small" checked={step.config?.allow_file_upload ?? false}
                            onChange={(e) => updateStep(step.id, { config: { ...step.config, allow_file_upload: e.target.checked } })} />}
                            label={<Typography variant="body2">Upload de arquivos</Typography>} />
                          <FormControlLabel control={<Switch size="small" checked={step.config?.allow_audio ?? false}
                            onChange={(e) => updateStep(step.id, { config: { ...step.config, allow_audio: e.target.checked } })} />}
                            label={<Typography variant="body2">Gravação de áudio</Typography>} />
                          <FormControlLabel control={<Switch size="small" checked={step.config?.ai_prefill_enabled ?? false}
                            onChange={(e) => updateStep(step.id, { config: { ...step.config, ai_prefill_enabled: e.target.checked } })} />}
                            label={<Typography variant="body2">AI Prefill</Typography>} />
                        </Stack>
                        {step.config?.allow_file_upload && (
                          <TextField
                            size="small"
                            label="Orientação para envio de documentos"
                            placeholder="Ex: Envie contratos, comprovantes de pagamento, prints de conversas..."
                            value={step.config?.upload_hint || ''}
                            onChange={(e) => updateStep(step.id, { config: { ...step.config, upload_hint: e.target.value } })}
                            fullWidth
                            multiline
                            rows={2}
                            helperText="Texto que aparecerá acima do botão de upload para orientar o usuário"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />
                        )}
                        {step.config?.allow_audio && (
                          <TextField
                            size="small"
                            label="Orientação para gravação de áudio"
                            placeholder="Ex: Descreva os fatos do caso, incluindo datas, valores e partes envolvidas..."
                            value={step.config?.audio_hint || ''}
                            onChange={(e) => updateStep(step.id, { config: { ...step.config, audio_hint: e.target.value } })}
                            fullWidth
                            multiline
                            rows={2}
                            helperText="Texto que aparecerá acima do botão de gravação para orientar o usuário"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />
                        )}
                      </Stack>
                    )}

                    {step.type === 'tribunal_selection' && (
                      <Stack spacing={1.5} sx={{ mb: 2 }}>
                        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                          Esses campos aparecem dentro da primeira etapa no formulário final, em um bloco separado.
                        </Alert>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          <FormControlLabel control={<Switch size="small" checked={step.config?.show_action_type ?? true}
                            onChange={(e) => updateStep(step.id, { config: { ...step.config, show_action_type: e.target.checked } })} />}
                            label={<Typography variant="body2">Tipo de ação</Typography>} />
                          <FormControlLabel control={<Switch size="small" checked={step.config?.show_rite ?? true}
                            onChange={(e) => updateStep(step.id, { config: { ...step.config, show_rite: e.target.checked } })} />}
                            label={<Typography variant="body2">Rito processual</Typography>} />
                          <FormControlLabel control={<Switch size="small" checked={step.config?.show_jurisdiction ?? true}
                            onChange={(e) => updateStep(step.id, { config: { ...step.config, show_jurisdiction: e.target.checked } })} />}
                            label={<Typography variant="body2">Competência / Comarca</Typography>} />
                        </Stack>
                      </Stack>
                    )}

                    {step.type === 'topics' && (
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel control={<Switch size="small" checked={step.config?.allow_custom_topics ?? true}
                          onChange={(e) => updateStep(step.id, { config: { ...step.config, allow_custom_topics: e.target.checked } })} />}
                          label={<Typography variant="body2">Permitir tópicos personalizados</Typography>} />
                      </Box>
                    )}

                    {step.type === 'requests' && (
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel control={<Switch size="small" checked={step.config?.allow_custom_requests ?? true}
                          onChange={(e) => updateStep(step.id, { config: { ...step.config, allow_custom_requests: e.target.checked } })} />}
                          label={<Typography variant="body2">Permitir pedidos personalizados</Typography>} />
                      </Box>
                    )}

                    {/* Fields editor - available for all step types */}
                    {(() => {
                      const stepFields = normalizeFields(step.config?.fields || []);
                      return (
                    <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Campos ({stepFields.length})
                      </Typography>
                      <Button size="small" startIcon={<AddIcon />} variant="outlined"
                        onClick={() => addFieldToStep(step.id)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                        Adicionar Campo
                      </Button>
                    </Box>

                    {stepFields.map((field: StepField, fieldIdx: number) => (
                      <Card key={`${step.id}_field_${fieldIdx}`} variant="outlined"
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragField && dragField.stepId === step.id && dragField.fieldId !== field.id) {
                            moveFieldInStepToIndex(step.id, dragField.fieldId, fieldIdx);
                          }
                          setDragField(null);
                        }}
                        onDragEnd={() => setDragField(null)}
                        sx={{
                          borderRadius: 2, mb: 1.5, overflow: 'visible',
                          opacity: dragField?.stepId === step.id && dragField?.fieldId === field.id ? 0.4 : 1,
                          transition: 'opacity 0.15s',
                        }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                              <IconButton size="small" disabled={fieldIdx === 0}
                                onClick={() => moveFieldInStep(step.id, field.id, 'up')}>
                                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              <Tooltip title="Arraste para reordenar">
                                <Box
                                  draggable
                                  onDragStart={() => setDragField({ stepId: step.id, fieldId: field.id })}
                                  onDragEnd={() => setDragField(null)}
                                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', px: 0.5 }}
                                >
                                  <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                                </Box>
                              </Tooltip>
                              <IconButton size="small" disabled={fieldIdx === stepFields.length - 1}
                                onClick={() => moveFieldInStep(step.id, field.id, 'down')}>
                                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Grid container spacing={1}>
                                <Grid item xs={4}>
                                  <TextField size="small" label="ID" value={field.id} fullWidth
                                    onChange={(e) => updateFieldInStep(step.id, field.id, { id: e.target.value })}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                </Grid>
                                <Grid item xs={4}>
                                  <TextField size="small" label="Label" value={field.label} fullWidth
                                    onChange={(e) => updateFieldInStep(step.id, field.id, { label: e.target.value })}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                </Grid>
                                <Grid item xs={2.5}>
                                  <TextField size="small" label="Tipo" value={field.type} select fullWidth
                                    onChange={(e) => updateFieldInStep(step.id, field.id, { type: e.target.value })}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
                                    {FIELD_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                  </TextField>
                                </Grid>
                                <Grid item xs={1.5}>
                                  <FormControlLabel sx={{ mt: 0.5 }}
                                    control={<Switch size="small" checked={field.required}
                                      onChange={(e) => updateFieldInStep(step.id, field.id, { required: e.target.checked })} />}
                                    label={<Typography variant="caption" fontWeight={600}>Obrig.</Typography>} />
                                </Grid>
                                {(field.type === 'text' || field.type === 'textarea') && (
                                  <>
                                    <Grid item xs={3}>
                                      <TextField size="small" label="Formato" value={field.format || ''} select fullWidth
                                        onChange={(e) => updateFieldInStep(step.id, field.id, { format: e.target.value || undefined })}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}>
                                        <MenuItem value="">Nenhum</MenuItem>
                                        {AVAILABLE_FORMATS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                                      </TextField>
                                    </Grid>
                                    <Grid item xs={3}>
                                      <TextField size="small" label="Mín. caracteres" type="number" value={field.minLength || ''} fullWidth
                                        onChange={(e) => updateFieldInStep(step.id, field.id, { minLength: e.target.value ? Number(e.target.value) : undefined })}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                    </Grid>
                                    <Grid item xs={3}>
                                      <TextField size="small" label="Máx. caracteres" type="number" value={field.maxLength || ''} fullWidth
                                        onChange={(e) => updateFieldInStep(step.id, field.id, { maxLength: e.target.value ? Number(e.target.value) : undefined })}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                    </Grid>
                                    <Grid item xs={3}>
                                      <TextField size="small" label="Placeholder" value={field.placeholder || ''} fullWidth
                                        onChange={(e) => updateFieldInStep(step.id, field.id, { placeholder: e.target.value })}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                    </Grid>
                                  </>
                                )}
                                {(field.type === 'select' || field.type === 'multiselect' || field.type === 'checkbox') && (
                                  <Grid item xs={12}>
                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: alpha('#1976d2', 0.02) }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                                          Opções ({(field.options || []).length})
                                        </Typography>
                                        <Button size="small" startIcon={<AddIcon />}
                                          onClick={() => {
                                            const opts = [...(field.options || []), { value: `opcao_${Date.now()}`, label: '' }];
                                            updateFieldInStep(step.id, field.id, { options: opts });
                                          }}
                                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
                                          Adicionar opção
                                        </Button>
                                      </Box>
                                      {(field.options || []).length === 0 && (
                                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
                                          Nenhuma opção. Clique em "Adicionar opção" acima.
                                        </Typography>
                                      )}
                                      {(field.options || []).map((opt, optIdx) => (
                                        <Box key={optIdx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                                          <TextField size="small" label="Valor" value={opt.value} sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                                            onChange={(e) => {
                                              const opts = [...(field.options || [])];
                                              opts[optIdx] = { ...opts[optIdx], value: e.target.value };
                                              updateFieldInStep(step.id, field.id, { options: opts });
                                            }} />
                                          <TextField size="small" label="Rótulo" value={opt.label} sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                                            onChange={(e) => {
                                              const opts = [...(field.options || [])];
                                              opts[optIdx] = { ...opts[optIdx], label: e.target.value };
                                              updateFieldInStep(step.id, field.id, { options: opts });
                                            }} />
                                          <IconButton size="small" onClick={() => {
                                            const opts = (field.options || []).filter((_, i) => i !== optIdx);
                                            updateFieldInStep(step.id, field.id, { options: opts });
                                          }} sx={{ '&:hover': { bgcolor: alpha('#f44336', 0.08), color: 'error.main' } }}>
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      ))}
                                      <FormControlLabel
                                        control={<Switch size="small" checked={!!field.allow_other}
                                          onChange={(e) => updateFieldInStep(step.id, field.id, { allow_other: e.target.checked })} />}
                                        label={<Typography variant="caption" fontWeight={600}>Permitir opção "Outro" (usuário digita)</Typography>}
                                        sx={{ mt: 0.5 }}
                                      />
                                      {field.allow_other && (
                                        <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                                          <TextField size="small" label="Rótulo do 'Outro'" value={field.other_label || ''} 
                                            placeholder="Outro" sx={{ flex: 1 }}
                                            onChange={(e) => updateFieldInStep(step.id, field.id, { other_label: e.target.value || undefined })}
                                            helperText="Ex: 'Outra opção', 'Especifique', 'Diferente'"
                                          />
                                          <TextField size="small" label="Tipo do campo" value={field.other_type || 'text'} select
                                            sx={{ minWidth: 140 }}
                                            onChange={(e) => updateFieldInStep(step.id, field.id, { other_type: e.target.value as 'text' | 'textarea' || undefined })}
                                          >
                                            <MenuItem value="text">Curto (1 linha)</MenuItem>
                                            <MenuItem value="textarea">Longo (múltiplas linhas)</MenuItem>
                                          </TextField>
                                        </Box>
                                      )}
                                    </Box>
                                  </Grid>
                                )}
                                {field.type === 'repeatable_group' && (
                                  <Grid item xs={12}>
                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: alpha('#4caf50', 0.02) }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                                          Sub-campos ({(field.subfields || []).length})
                                        </Typography>
                                        <Button size="small" startIcon={<AddIcon />}
                                          onClick={() => {
                                            const subs = [...(field.subfields || []), { id: `sub_${Date.now()}`, label: '', type: 'text', required: false, order: (field.subfields || []).length } as StepField];
                                            updateFieldInStep(step.id, field.id, { subfields: subs });
                                          }}
                                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
                                          Adicionar sub-campo
                                        </Button>
                                      </Box>
                                      {(field.subfields || []).length === 0 && (
                                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
                                          Nenhum sub-campo. Clique em "Adicionar sub-campo" acima.
                                        </Typography>
                                      )}
                                      {(field.subfields || []).map((sf: StepField, sfIdx: number) => (
                                        <Box key={sfIdx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                                          <TextField size="small" label="ID" value={sf.id} sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                                            onChange={(e) => {
                                              const subs = [...(field.subfields || [])];
                                              subs[sfIdx] = { ...subs[sfIdx], id: e.target.value };
                                              updateFieldInStep(step.id, field.id, { subfields: subs });
                                            }} />
                                          <TextField size="small" label="Label" value={sf.label} sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                                            onChange={(e) => {
                                              const subs = [...(field.subfields || [])];
                                              subs[sfIdx] = { ...subs[sfIdx], label: e.target.value };
                                              updateFieldInStep(step.id, field.id, { subfields: subs });
                                            }} />
                                          <TextField size="small" label="Tipo" value={sf.type} select sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                                            onChange={(e) => {
                                              const subs = [...(field.subfields || [])];
                                              subs[sfIdx] = { ...subs[sfIdx], type: e.target.value };
                                              updateFieldInStep(step.id, field.id, { subfields: subs });
                                            }}>
                                            {FIELD_TYPES.filter(t => t.value !== 'repeatable_group').map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                          </TextField>
                                          <FormControlLabel sx={{ mr: 0 }}
                                            control={<Switch size="small" checked={sf.required}
                                              onChange={(e) => {
                                                const subs = [...(field.subfields || [])];
                                                subs[sfIdx] = { ...subs[sfIdx], required: e.target.checked };
                                                updateFieldInStep(step.id, field.id, { subfields: subs });
                                              }} />}
                                            label={<Typography variant="caption" fontWeight={600}>Obrig.</Typography>} />
                                          <IconButton size="small" onClick={() => {
                                            const subs = (field.subfields || []).filter((_: any, i: number) => i !== sfIdx);
                                            updateFieldInStep(step.id, field.id, { subfields: subs });
                                          }} sx={{ '&:hover': { bgcolor: alpha('#f44336', 0.08), color: 'error.main' } }}>
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Grid>
                                )}
                                <Grid item xs={12}>
                                  <TextField size="small" label="Texto de ajuda" value={field.helpText || ''} fullWidth
                                    onChange={(e) => updateFieldInStep(step.id, field.id, { helpText: e.target.value })}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField size="small" label="Descrição interna (admin)" value={field.description || ''} fullWidth
                                    placeholder="Notas internas sobre este campo (não visível para o usuário)"
                                    onChange={(e) => updateFieldInStep(step.id, field.id, { description: e.target.value })}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                                    helperText="Visível apenas no admin, para documentação do fluxo" />
                                </Grid>
                              </Grid>
                            </Box>
                            <Tooltip title="Remover campo">
                              <IconButton size="small" onClick={() => removeFieldFromStep(step.id, field.id)}
                                sx={{ '&:hover': { bgcolor: alpha('#f44336', 0.08), color: 'error.main' } }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                    {stepFields.length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Button size="small" startIcon={<AddIcon />} variant="outlined"
                          onClick={() => addFieldToStep(step.id)}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                          Adicionar Campo
                        </Button>
                      </Box>
                    )}
                    </>
                      );
                    })()}
                  </Stack>
                </Box>
              </Collapse>
            </Paper>
          );
        })}

        {/* Locked steps indicator */}
        <Paper variant="outlined" sx={{ borderRadius: 2.5, p: 2, mt: 1, opacity: 0.6, bgcolor: alpha(theme.palette.action.disabled, 0.03) }}>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip label={getSteps().length + 1} size="small" sx={{ fontWeight: 700, minWidth: 28 }} />
              <TuneIcon sx={{ color: 'text.disabled' }} />
              <Typography variant="body2" fontWeight={600} color="text.secondary">Configuração do Documento (fixo)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip label={getSteps().length + 2} size="small" sx={{ fontWeight: 700, minWidth: 28 }} />
              <DescriptionIcon sx={{ color: 'text.disabled' }} />
              <Typography variant="body2" fontWeight={600} color="text.secondary">Revisão e Geração (fixo)</Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Add Step Dialog */}
        <Dialog open={addStepDialog} onClose={() => setAddStepDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Adicionar Etapa</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Tipos especiais</Typography>
            <Grid container spacing={1} sx={{ mb: 3 }}>
              {[
                { type: 'tribunal_selection' as const, label: 'Seleção de Tribunal', icon: <GavelIcon />, desc: 'Seleção de tribunal, tipo de ação e rito' },
                { type: 'topics' as const, label: 'Tópicos Jurídicos', icon: <TopicIcon />, desc: 'Tópicos pré-definidos e personalizados' },
                { type: 'requests' as const, label: 'Pedidos', icon: <ListAltIcon />, desc: 'Pedidos pré-definidos e personalizados' },
              ].map(t => (
                <Grid item xs={4} key={t.type}>
                  <Card variant="outlined" sx={{ cursor: 'pointer', borderRadius: 2, transition: 'all 0.15s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                    onClick={() => addStep({ type: t.type, title: t.label, icon: t.type === 'tribunal_selection' ? 'gavel' : t.type === 'topics' ? 'topic' : 'request_quote', config: {} })}>
                    <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                      <Box sx={{ color: 'primary.main', mb: 1 }}>{t.icon}</Box>
                      <Typography variant="body2" fontWeight={700}>{t.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.desc}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Templates de campos</Typography>
            <Grid container spacing={1}>
              {STEP_TEMPLATES.map((tpl, i) => (
                <Grid item xs={6} sm={4} key={i}>
                  <Card variant="outlined" sx={{ cursor: 'pointer', borderRadius: 2, transition: 'all 0.15s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                    onClick={() => addStep(tpl.step)}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Box sx={{ color: 'primary.main', mb: 0.5 }}>
                        {STEP_TYPE_ICONS[tpl.icon] || <EditNoteIcon />}
                      </Box>
                      <Typography variant="body2" fontWeight={700}>{tpl.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{tpl.description}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddStepDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Tab 2: AI Config */}
      <TabPanel value={tab} index={2}>
        <SectionCard icon={<TuneIcon />} title="Pipeline de Geração" subtitle="Modo de geração, temperatura e limites">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Modo de Geração" value={flow.generation_mode || 'simple'} select fullWidth
                onChange={(e) => updateField('generation_mode', e.target.value)}
                helperText={
                  flow.generation_mode === 'advanced' ? 'Outline → Geração → Revisão (3 chamadas)' :
                  flow.generation_mode === 'sectional' ? 'Gera cada seção separadamente (N+2 chamadas)' :
                  'Uma única chamada ao LLM'
                }
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <MenuItem value="simple">Simples (1 chamada)</MenuItem>
                <MenuItem value="advanced">Avançado (3 chamadas)</MenuItem>
                <MenuItem value="sectional">Seccional (por seção)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Perfil de Temperatura" value={flow.ai_temperature_preset || 'balanced'} select fullWidth
                onChange={(e) => updateField('ai_temperature_preset', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <MenuItem value="conservative">Conservador (0.15) - Requerimentos, certidões</MenuItem>
                <MenuItem value="balanced">Equilibrado (0.35) - Petições, contestações</MenuItem>
                <MenuItem value="creative">Criativo (0.55) - Pareceres, memoriais</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ px: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Limite de Tokens:</Typography>
                  <Chip label={flow.ai_max_tokens || 12000} size="small" sx={{ fontWeight: 700 }} />
                </Box>
                <Slider value={flow.ai_max_tokens || 12000} min={4000} max={32000} step={1000}
                  onChange={(_, v) => updateField('ai_max_tokens', v)}
                  valueLabelDisplay="auto" />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel control={<Switch checked={flow.ai_validation_enabled || false}
                onChange={(e) => updateField('ai_validation_enabled', e.target.checked)} />}
                label={<Box>
                  <Typography variant="body2" fontWeight={600}>Validação pós-geração</Typography>
                  <Typography variant="caption" color="text.secondary">LLM revisa o documento final</Typography>
                </Box>} />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard icon={<PsychologyIcon />} title="Contexto e Instruções" subtitle="Direcionamento para o LLM durante a geração" defaultExpanded>
          <Stack spacing={2.5}>
            <AutoGrowTextField label="Contexto da Área Jurídica" value={flow.ai_context || ''} fullWidth minRows={4} maxRows={18}
              onChange={(e) => updateField('ai_context', e.target.value)}
              helperText="Legislação aplicável, artigos relevantes e contexto específico"
            />
            <AutoGrowTextField label="Dicas de Prompt" value={flow.ai_prompt_hints || ''} fullWidth minRows={3} maxRows={16}
              onChange={(e) => updateField('ai_prompt_hints', e.target.value)}
              helperText="Orientações extras para melhorar a qualidade da geração"
            />
            <AutoGrowTextField label="Instruções de Sistema (Quality Checklist)" value={flow.ai_system_instructions || ''} fullWidth minRows={3} maxRows={16}
              onChange={(e) => updateField('ai_system_instructions', e.target.value)}
              helperText="Checklist de qualidade que o LLM deve seguir"
            />
            <AutoGrowTextField label="Estrutura do Documento" value={flow.ai_document_structure || ''} fullWidth minRows={4} maxRows={18}
              onChange={(e) => updateField('ai_document_structure', e.target.value)}
              helperText="Seções obrigatórias e formato esperado do documento"
            />
            <AutoGrowTextField label="Exemplo de Saída (Few-Shot)" value={flow.ai_example_output || ''} fullWidth minRows={4} maxRows={18}
              onChange={(e) => updateField('ai_example_output', e.target.value)}
              helperText="Um exemplo de documento bem gerado para guiar o LLM"
            />
          </Stack>
        </SectionCard>

        <SectionCard icon={<UploadFileIcon />} title="Import Inteligente (Prefill)"
          subtitle="Usar IA para extrair dados de documentos importados"
          enabled={flow.ai_prefill_enabled ?? true}
          onToggle={(v) => updateField('ai_prefill_enabled', v)}>
          <AutoGrowTextField label="Instruções de Prefill" value={flow.ai_prefill_instructions || ''} fullWidth minRows={3} maxRows={16}
            onChange={(e) => updateField('ai_prefill_instructions', e.target.value)}
            helperText="Como a IA deve mapear dados do documento importado para os campos do formulário"
          />
        </SectionCard>

        <SectionCard icon={<AutoAwesomeIcon />} title="Análise de Uploads"
          subtitle="IA analisa arquivos para sugerir áreas, tópicos e pedidos"
          enabled={flow.ai_upload_analysis_enabled ?? true}
          onToggle={(v) => updateField('ai_upload_analysis_enabled', v)}>
          <AutoGrowTextField label="Instruções de Análise" value={flow.ai_upload_analysis_instructions || ''} fullWidth minRows={3} maxRows={16}
            onChange={(e) => updateField('ai_upload_analysis_instructions', e.target.value)}
            helperText="Quais sugestões a IA deve gerar a partir dos documentos enviados"
          />
        </SectionCard>

        <SectionCard icon={<SmartToyIcon />} title="Extração de Contexto"
          subtitle="Foco da extração inteligente de documentos longos">
          <AutoGrowTextField label="Foco da Extração" value={flow.ai_context_extraction_focus || ''} fullWidth minRows={3} maxRows={16}
            onChange={(e) => updateField('ai_context_extraction_focus', e.target.value)}
            helperText="Quais informações priorizar ao processar documentos extensos (ex: nomes, datas, valores, cláusulas)"
          />
        </SectionCard>

        {/* AI Suggestions Panel */}
        {!isNew && (
          <SectionCard icon={<AutoAwesomeIcon />} title="Sugestões da IA"
            subtitle="Solicite a IA sugestões de campos, etapas e dicas para melhorar o fluxo">
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: 'column' }}>
              <Button variant="outlined" startIcon={aiSuggestionsLoading ? <CircularProgress size={16} /> : <SmartToyIcon />}
                disabled={aiSuggestionsLoading}
                onClick={async () => {
                  setAiSuggestionsLoading(true);
                  try {
                    const res = await flowService.suggestFields(flow.id || 'new', {
                      flow_name: flow.name || '', area: flow.area || '', category: flow.category || '',
                      steps: flow.steps_config?.steps || [],
                    });
                    setAiSuggestions(res.suggestions);
                  } catch (e) { console.error(e); }
                  finally { setAiSuggestionsLoading(false); }
                }}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                {aiSuggestionsLoading ? 'Analisando...' : 'Solicitar Sugestões'}
              </Button>
              {aiSuggestions.length > 0 && (
                <Stack spacing={1} sx={{ width: '100%' }}>
                  {aiSuggestions.map((s: any, i: number) => (
                    <Alert key={i} severity={s.type === 'missing_field' ? 'warning' : s.type === 'missing_step' ? 'info' : 'success'}
                      variant="outlined" sx={{ borderRadius: 2 }}>
                      <Typography variant="body2">{s.message}</Typography>
                      {s.step_id && <Typography variant="caption" color="text.secondary">Etapa: {s.step_id}</Typography>}
                    </Alert>
                  ))}
                </Stack>
              )}
            </Box>
          </SectionCard>
        )}

        {/* Prompt Preview Panel */}
        {!isNew && (
          <SectionCard icon={<VisibilityIcon />} title="Pré-visualização do Prompt"
            subtitle="Veja como o prompt será montado para a geração">
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Button variant="outlined" startIcon={promptPreviewLoading ? <CircularProgress size={16} /> : <VisibilityIcon />}
                disabled={promptPreviewLoading}
                onClick={async () => {
                  setPromptPreviewLoading(true);
                  try {
                    const preview = await flowService.previewPrompt(flow.id!, {});
                    setPromptPreview(preview);
                  } catch (e) { console.error(e); }
                  finally { setPromptPreviewLoading(false); }
                }}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                {promptPreviewLoading ? 'Gerando...' : 'Gerar Prévia'}
              </Button>
              {promptPreview && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Chip label={`~${promptPreview.estimated_tokens} tokens`} size="small" color="info" />
                    <Chip label={`~${promptPreview.estimated_cost_credits} créditos`} size="small" color="warning" />
                  </Box>
                  <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>System Prompt</Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, maxHeight: 300, overflow: 'auto', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {promptPreview.system_prompt}
                    </Typography>
                  </Paper>
                  <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>User Prompt</Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxHeight: 300, overflow: 'auto', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {promptPreview.user_prompt}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          </SectionCard>
        )}
      </TabPanel>

      {/* Tab 3: Analytics */}
      {!isNew && (
        <TabPanel value={tab} index={3}>
          {analytics && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Usos Totais', value: analytics.total_uses, icon: <TrendingUpIcon />, color: theme.palette.primary.main },
                { label: 'Rascunhos Ativos', value: analytics.total_drafts, icon: <DraftsIcon />, color: theme.palette.warning.main },
                { label: 'Favoritos', value: analytics.total_favorites, icon: <FavoriteIcon />, color: '#f44336' },
                { label: 'Taxa de Conclusão', value: analytics.completion_rate != null ? `${(analytics.completion_rate * 100).toFixed(0)}%` : 'N/A', icon: <StarIcon />, color: theme.palette.success.main },
              ].map((m, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Card variant="outlined" sx={{ borderRadius: 2.5, textAlign: 'center' }}>
                    <CardContent sx={{ py: 2 }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: 2, mx: 'auto', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(m.color, 0.1), color: m.color }}>
                        {m.icon}
                      </Box>
                      <Typography variant="h4" fontWeight={800} color={m.color}>{m.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <SectionCard icon={<HistoryIcon />} title="Histórico de Alterações" subtitle="Auditoria de todas as mudanças neste fluxo">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Usuário</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ação</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Detalhes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow><TableCell colSpan={4} sx={{ py: 4, textAlign: 'center' }}>
                    <HistoryIcon sx={{ fontSize: 32, color: 'action.disabled', mb: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">Nenhum registro de alteração</Typography>
                  </TableCell></TableRow>
                ) : auditLogs.map(log => (
                  <TableRow key={log.id} hover>
                    <TableCell><Typography variant="body2">{new Date(log.created_at).toLocaleString('pt-BR')}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{log.user_name || log.user_id}</Typography></TableCell>
                    <TableCell><Chip label={log.action} size="small" variant="outlined" sx={{ borderRadius: 1.5, fontWeight: 600 }} /></TableCell>
                    <TableCell>
                      {log.changes ? (
                        <Tooltip title={<pre style={{ fontSize: '0.7rem', margin: 0 }}>{JSON.stringify(log.changes, null, 2)}</pre>}>
                          <Chip label={`${Object.keys(log.changes).length} campo(s)`} size="small"
                            sx={{ cursor: 'pointer', borderRadius: 1.5 }} />
                        </Tooltip>
                      ) : <Typography variant="caption" color="text.disabled">-</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>
        </TabPanel>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentFlowEditorPage;
